import React from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputAdornment
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper/Paper";
import PageContainer from "../../Components/PageContainer/PageContainer";
import { CustomAlert, CustomTextFieldV2 } from "../../Components/formComponents";
import { useAuthProvider } from "../../providers";
import { useFormik } from "formik";
import PersonIcon from '@mui/icons-material/Person';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { createNewPublicUserUserAsync } from "../../redux/settings/user-profile/user-profile.actions";
import { useAppDispatch } from "../../redux";
import { defaultCreateUser } from "../../redux/settings/user-profile";
import { useQueryParams } from "../../hooks";


export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();
  const outletUUID = useQueryParams("outlet")


  const {
    values,
    errors,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues: defaultCreateUser,
    validate: (values) => {
      let errors: any = {}
      if (!values.first_name) {
        errors.first_name = "First name is required.";
      } if (!values.last_name) {
        errors.last_name = "Last name is required.";
      }
      if (!values.email) {
        errors.email = "Email is required.";
      } else if (
        values.email !== "" &&
        !/^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i.test(
          values.email,
        )
      ) {
        errors.email = "*Invalid email address.";
      }
      if (!values.user_password) {
        errors.user_password = "Password is required.";
      } else if (values.user_password.length < 6) {
        errors.user_password = "Password must be at least 6 characters long.";
      }
      if (!values.confirmPassword) {
        errors.confirmPassword = "Confirm password is required.";
      } else if (values.user_password !== values.confirmPassword) {
        errors.confirmPassword = "Passwords are not matching.";
      }
      return errors
    },
    onSubmit: async (values) => {
      setLoading(true);
      dispatch(createNewPublicUserUserAsync({
        payload: {
          ...values,
          outlet_details: [
            {
              "outlet_name": "",
              "outlet_uuid": outletUUID || ""
            }
          ]
        },
        onSuccess(isSuccess, data) {
          if (isSuccess) {
            navigate("/auth/login");
          } else {

          }
        },
      })).finally(() => setLoading(false));

    },
  });

  return (
    <PageContainer title="Sign up" description="This is Sign up page">
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper
          sx={{
            p: 4,
            pt: 2,
            pb: 2,
            border: "none rgb(230, 235, 241)",
            borderRadius: "4px",
            boxShadow: "rgb(0 0 0 / 8%) 0px 1px 4px",
            background: "#fff",
            maxWidth: 400
          }}
        >
          <Typography variant="h4" pb={2} fontSize={18} fontWeight={800} align="center" sx={{ color: "#000" }} >
            Create a new account
          </Typography>

          {error && (
            <CustomAlert
              sx={{ mt: 2 }}
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </CustomAlert>
          )}
          <form onSubmit={handleSubmit}>
            <>
              <Box sx={{ display: 'flex', justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                <CustomTextFieldV2
                  id="first_name"
                  variant="outlined"
                  placeholder="enter first name"
                  value={values.first_name}
                  fullWidth
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      height: 4
                    },
                  }}

                />
                <CustomTextFieldV2
                  id="last_name"
                  variant="outlined"
                  placeholder="enter last name"
                  value={values.last_name}
                  fullWidth
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  onChange={handleChange}
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      height: 4
                    },
                  }}
                />
              </Box>
              <CustomTextFieldV2
                id="email"
                variant="outlined"
                placeholder="enter email address"
                value={values.email}
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment sx={{ background: "#252525" }} position="end"><PersonIcon /></InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-input": {
                    height: 4
                  },
                }}
              />
              <CustomTextFieldV2
                id="user_password"
                variant="outlined"
                placeholder="enter password"
                fullWidth
                value={values.user_password}
                // type="password"
                error={!!errors.user_password}
                helperText={errors.user_password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment sx={{ background: "#252525" }} position="end"><LockOpenIcon /></InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-input": {
                    height: 4
                  },
                }}
              />
              <CustomTextFieldV2
                id="confirmPassword"
                variant="outlined"
                placeholder="enter confirm password"
                fullWidth
                value={values.confirmPassword}
                // type="password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment sx={{ background: "#252525" }} position="end"><LockOpenIcon /></InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  "& .MuiOutlinedInput-input": {
                    height: 4
                  },
                }}
              />
              <Box sx={{ mt: 2 }} display="flex" justifyContent={"end"}>
                <Typography
                  component={Link}
                  to={`/auth/login?outlet=${outletUUID}`}
                  fontWeight="500"
                  sx={{
                    display: "block",
                    textDecoration: "none",
                    color: "#000",
                    ":hover": {
                      color: "blue",
                    }
                  }}
                >
                  Already have accoount ? Login
                </Typography>
              </Box>
              <Button
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                type="submit"
                sx={{
                  mt: 2,
                  pt: "10px",
                  pb: "10px",
                  background: "#000",
                  color: "#fff",
                  "&:hover": {
                    background: "#252525",
                    color: "#fff",
                  }
                }}
              >
                Sign Up
              </Button>
            </>
          </form>
        </Paper>
      </Box>
    </PageContainer>
  );
};